/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import React, {FunctionComponent, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getAEMResource} from "../../selectors/generic";
import {ISearchResults, ISearchResultsModel} from "../../interfaces/pages/ISearchResults";
import {fetchGeneralSearchResults} from "../../actions/search";
import {
    selectGeneralSearchRedirect,
    selectGeneralSearchResults,
    selectGeneralSearchResultsCount,
    selectGeneralSearchResultsTags
} from "../../selectors/search";
import {Finder} from "../templates/Finder";
import {ConnectedRouter} from "connected-react-router";
import {browserHistory} from "../../config/redux.store";
import {SkeletonTypes} from "../atoms/effects/skeletons/utils/SkeletonTypes";
import {SearchGridCard} from "../molecules/cards/SearchGridCard";
import {isLoadingByScope, Loader} from "../../selectors/loader";
import {activateFinder, setSearch} from "../../actions/finder";
import {FILTER_TYPES} from "../../data/finders.model";
import {getSearch, getTagFilter, hasFiltering, isLoaded} from "../../selectors/finder";
import {fetchTags} from "../../actions/aem";
import {areTagLoaded} from "../../selectors/tags";
import {GirdListCtaCard} from "../molecules/cards/GirdListCtaCard";

export const SearchResults: FunctionComponent<ISearchResults> = ({model}) => {

    const dispatch = useDispatch();

    const [limit, setLimit] = useState(12);

    const resource: ISearchResultsModel = useSelector(state => getAEMResource(state, model));
    const loading: boolean = useSelector(state => isLoadingByScope(state, Loader.searchResults));
    const searchResults: any[any] = useSelector(selectGeneralSearchResults);
    const availableTags: any[] = useSelector(selectGeneralSearchResultsTags);
    const redirectPath: Location = useSelector(selectGeneralSearchRedirect);
    const tagsLoaded: boolean = useSelector(areTagLoaded);
    const hasFilter: boolean = useSelector(hasFiltering);
    const resultCount: number = useSelector(selectGeneralSearchResultsCount);
    const filterValues: any[] = useSelector(state => getTagFilter(state, 0));
    const isFinderLoaded: boolean = useSelector(isLoaded);
    const searchTerm: string = useSelector(getSearch) || "";

    useEffect(() => {
        dispatch(fetchTags());
    }, []);

    useEffect(() => {
        if (redirectPath) window.location = redirectPath;
    }, [redirectPath]);

    useEffect(() => {
        setLimit(resource?.maxResults);
    }, [resource]);

    useEffect(() => {
        if (tagsLoaded) dispatch(activateFinder({ type: 'search', filters: [{...resource, type: FILTER_TYPES.BASIC_LIST_FILTER}] }));
    }, [tagsLoaded]);

    useEffect(() => {
        if (isFinderLoaded) getResults();
    }, [filterValues, searchTerm]);

    const renderCard = ({item}) => {
        return <SearchGridCard result={item}/>;
    }

    const paginationCallback = (page) => {
        getResults((page * limit) - limit);
    }

    const getResults = (offset = 0) => {
        dispatch(fetchGeneralSearchResults({
            model,
            search: searchTerm,
            root: resource?.rootPath,
            tags: Object.entries(filterValues)?.filter(([key, value]) => value === true)?.map(([key, val]) => key) || [],
            limit: resource?.maxResults,
            offset: offset
        }));
    }

    if (resource == null) return null;

    return <ConnectedRouter history={browserHistory}>
        <Finder resource={resource}
                items={searchResults}
                card={renderCard}
                loading={loading}
                hasFiltering={hasFilter}
                availableTags={availableTags}
                skeletonType={SkeletonTypes.SEARCH_FINDER}
                paginationCallback={paginationCallback}
                progressivePagination
                paginationAmount={resultCount}
                ctaCard={<GirdListCtaCard ctaTitle={resource?.noResultsMessage}/>}
                showCtaNoResultsOnly
                showSearchItemAsFilter/>
    </ConnectedRouter>;
}

export default SearchResults;
